import { useState, useEffect, useMemo } from "react";

/**
 * Throughput Report - Analyze production throughput by work center and operation
 * Shows jobs completed per period, cycle times, bottleneck identification
 */
export default function ThroughputReport({ onBackToApp }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  // Filters
  const [period, setPeriod] = useState("day");
  const [dateRange, setDateRange] = useState("month");
  const [customStartDate, setCustomStartDate] = useState("");
  const [customEndDate, setCustomEndDate] = useState("");
  const [workCenterFilter, setWorkCenterFilter] = useState("");

  // Tab view
  const [activeTab, setActiveTab] = useState("overview");

  const getDateParams = () => {
    const now = new Date();
    let startDate, endDate;

    switch (dateRange) {
      case "today":
        startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate()).toISOString();
        break;
      case "week":
        const weekAgo = new Date(now);
        weekAgo.setDate(weekAgo.getDate() - 7);
        startDate = weekAgo.toISOString();
        break;
      case "month":
        const monthAgo = new Date(now);
        monthAgo.setMonth(monthAgo.getMonth() - 1);
        startDate = monthAgo.toISOString();
        break;
      case "quarter":
        const quarterAgo = new Date(now);
        quarterAgo.setMonth(quarterAgo.getMonth() - 3);
        startDate = quarterAgo.toISOString();
        break;
      case "custom":
        startDate = customStartDate ? new Date(customStartDate).toISOString() : undefined;
        endDate = customEndDate ? new Date(customEndDate + "T23:59:59").toISOString() : undefined;
        break;
      default:
        break;
    }

    return { startDate, endDate };
  };

  useEffect(() => {
    loadData();
  }, [period, dateRange, customStartDate, customEndDate, workCenterFilter]);

  const loadData = async () => {
    setLoading(true);
    setError(null);

    try {
      const token = sessionStorage.getItem("msal.backendToken");
      const { startDate, endDate } = getDateParams();

      let url = `/api/reports/throughput?period=${period}`;
      if (startDate) url += `&startDate=${encodeURIComponent(startDate)}`;
      if (endDate) url += `&endDate=${encodeURIComponent(endDate)}`;
      if (workCenterFilter) url += `&workCenter=${encodeURIComponent(workCenterFilter)}`;

      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) throw new Error("Failed to load throughput data");

      const result = await res.json();
      setData(result);
    } catch (err) {
      console.error("Error loading throughput:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Format duration
  const formatDuration = (minutes) => {
    if (!minutes) return "0m";
    const hrs = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    if (hrs > 0) {
      return `${hrs}h ${mins}m`;
    }
    return `${mins}m`;
  };

  // Get work centers for filter dropdown
  const workCenters = useMemo(() => {
    if (!data?.byWorkCenter) return [];
    return data.byWorkCenter.map(wc => wc.workCenter).sort();
  }, [data]);

  return (
    <div className="flex-1 overflow-auto bg-slate-100 text-gray-900 flex flex-col">

      {/* CONTROLS BAR */}
      <div className="bg-white border-b border-gray-200 px-4 py-3">
        <div className="max-w-7xl mx-auto flex items-center gap-4 flex-wrap">
          {/* Period selector */}
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-500">Period:</label>
            <select
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
              className="bg-slate-100 border border-gray-300 rounded-md px-3 py-1.5 text-sm"
            >
              <option value="hour">Hourly</option>
              <option value="day">Daily</option>
              <option value="week">Weekly</option>
              <option value="month">Monthly</option>
            </select>
          </div>

          {/* Date range */}
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-500">Range:</label>
            <select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
              className="bg-slate-100 border border-gray-300 rounded-md px-3 py-1.5 text-sm"
            >
              <option value="today">Today</option>
              <option value="week">Last 7 Days</option>
              <option value="month">Last 30 Days</option>
              <option value="quarter">Last 90 Days</option>
              <option value="all">All Time</option>
              <option value="custom">Custom...</option>
            </select>
          </div>

          {/* Custom date inputs */}
          {dateRange === "custom" && (
            <div className="flex items-center gap-2">
              <input
                type="date"
                value={customStartDate}
                onChange={(e) => setCustomStartDate(e.target.value)}
                className="bg-slate-100 border border-gray-300 rounded-md px-2 py-1.5 text-sm"
              />
              <span className="text-gray-500">to</span>
              <input
                type="date"
                value={customEndDate}
                onChange={(e) => setCustomEndDate(e.target.value)}
                className="bg-slate-100 border border-gray-300 rounded-md px-2 py-1.5 text-sm"
              />
            </div>
          )}

          {/* Work center filter */}
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-500">Work Center:</label>
            <select
              value={workCenterFilter}
              onChange={(e) => setWorkCenterFilter(e.target.value)}
              className="bg-slate-100 border border-gray-300 rounded-md px-3 py-1.5 text-sm"
            >
              <option value="">All Work Centers</option>
              {workCenters.map(wc => (
                <option key={wc} value={wc}>{wc}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* SUMMARY STATS */}
      {data?.summary && (
        <div className="bg-slate-100 border-b border-gray-200 py-4 px-4">
          <div className="max-w-7xl mx-auto grid grid-cols-2 md:grid-cols-5 gap-4 text-center">
            <div>
              <div className="text-3xl font-bold text-blue-400">
                {data.summary.totalJobsCompleted?.toLocaleString() || 0}
              </div>
              <div className="text-xs text-gray-500">Jobs Completed</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-green-400">
                {data.summary.totalTickets?.toLocaleString() || 0}
              </div>
              <div className="text-xs text-gray-500">Tickets</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-purple-600">
                {formatDuration(data.summary.avgCycleTimeMinutes)}
              </div>
              <div className="text-xs text-gray-500">Avg Cycle Time</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-cyan-600">
                {data.summary.totalHours?.toLocaleString() || 0}h
              </div>
              <div className="text-xs text-gray-500">Total Hours</div>
            </div>
            {data.summary.bottleneckWorkCenter && (
              <div>
                <div className="text-xl font-bold text-yellow-400">
                  {data.summary.bottleneckWorkCenter}
                </div>
                <div className="text-xs text-gray-500">
                  Bottleneck ({formatDuration(data.summary.bottleneckAvgCycleMinutes)} avg)
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* TABS */}
      <div className="bg-white border-b border-gray-200 px-4">
        <div className="max-w-7xl mx-auto flex items-center gap-1">
          {[
            { id: "overview", label: "By Work Center" },
            { id: "operations", label: "By Operation" },
            { id: "trend", label: "Trend" },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.id
                  ? "border-blue-500 text-blue-400"
                  : "border-transparent text-gray-500 hover:text-gray-900"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* MAIN CONTENT */}
      <main className="flex-1 overflow-auto p-4">
        <div className="max-w-7xl mx-auto">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-gray-500">Loading throughput data...</div>
            </div>
          ) : error ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-red-400">{error}</div>
            </div>
          ) : (
            <>
              {/* BY WORK CENTER TAB */}
              {activeTab === "overview" && data?.byWorkCenter && (
                <div className="bg-slate-100 rounded-lg overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-white">
                      <tr>
                        <th className="text-left px-4 py-3 text-sm font-medium text-gray-500">Work Center</th>
                        <th className="text-right px-4 py-3 text-sm font-medium text-gray-500">Jobs</th>
                        <th className="text-right px-4 py-3 text-sm font-medium text-gray-500">Tickets</th>
                        <th className="text-right px-4 py-3 text-sm font-medium text-gray-500">Avg Cycle</th>
                        <th className="text-right px-4 py-3 text-sm font-medium text-gray-500">Jobs/Hour</th>
                        <th className="text-right px-4 py-3 text-sm font-medium text-gray-500">Total Hours</th>
                        <th className="text-right px-4 py-3 text-sm font-medium text-gray-500">Pieces</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {data.byWorkCenter.map((wc, idx) => (
                        <tr key={wc.workCenter || idx} className="hover:bg-slate-100/30">
                          <td className="px-4 py-3">
                            <span className="font-medium">{wc.workCenter || "Unknown"}</span>
                          </td>
                          <td className="px-4 py-3 text-right text-blue-400 font-mono">
                            {wc.jobsCompleted?.toLocaleString()}
                          </td>
                          <td className="px-4 py-3 text-right text-gray-700 font-mono">
                            {wc.ticketCount?.toLocaleString()}
                          </td>
                          <td className="px-4 py-3 text-right text-purple-600">
                            {formatDuration(wc.avgCycleTimeMinutes)}
                          </td>
                          <td className="px-4 py-3 text-right text-green-400 font-mono">
                            {wc.jobsPerHour?.toFixed(2)}
                          </td>
                          <td className="px-4 py-3 text-right text-gray-700 font-mono">
                            {Math.round((wc.totalSeconds || 0) / 3600).toLocaleString()}
                          </td>
                          <td className="px-4 py-3 text-right text-gray-500 font-mono">
                            {wc.totalPieces?.toLocaleString() || 0}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* BY OPERATION TAB */}
              {activeTab === "operations" && data?.byOperation && (
                <div className="bg-slate-100 rounded-lg overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-white">
                      <tr>
                        <th className="text-left px-4 py-3 text-sm font-medium text-gray-500">Operation</th>
                        <th className="text-left px-4 py-3 text-sm font-medium text-gray-500">Work Center</th>
                        <th className="text-right px-4 py-3 text-sm font-medium text-gray-500">Tickets</th>
                        <th className="text-right px-4 py-3 text-sm font-medium text-gray-500">Avg Cycle</th>
                        <th className="text-right px-4 py-3 text-sm font-medium text-gray-500">Min</th>
                        <th className="text-right px-4 py-3 text-sm font-medium text-gray-500">Max</th>
                        <th className="text-right px-4 py-3 text-sm font-medium text-gray-500">Pieces</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {data.byOperation.map((op, idx) => (
                        <tr key={`${op.operationCode}-${op.workCenter}-${idx}`} className="hover:bg-slate-100/30">
                          <td className="px-4 py-3">
                            <span className="font-medium text-purple-600">{op.operationCode}</span>
                          </td>
                          <td className="px-4 py-3 text-gray-500">{op.workCenter}</td>
                          <td className="px-4 py-3 text-right text-blue-400 font-mono">
                            {op.ticketCount?.toLocaleString()}
                          </td>
                          <td className="px-4 py-3 text-right text-purple-600">
                            {formatDuration(op.avgCycleTimeMinutes)}
                          </td>
                          <td className="px-4 py-3 text-right text-green-400 text-sm">
                            {formatDuration(op.minCycleTimeMinutes)}
                          </td>
                          <td className="px-4 py-3 text-right text-red-400 text-sm">
                            {formatDuration(op.maxCycleTimeMinutes)}
                          </td>
                          <td className="px-4 py-3 text-right text-gray-500 font-mono">
                            {op.totalPieces?.toLocaleString() || 0}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* TREND TAB */}
              {activeTab === "trend" && data?.trend && (
                <div className="space-y-4">
                  {/* Simple bar visualization */}
                  <div className="bg-slate-100 rounded-lg p-4">
                    <h3 className="text-sm font-medium text-gray-500 mb-4">Completed Jobs Over Time</h3>
                    <div className="space-y-2">
                      {data.trend.slice(-20).map((point, idx) => {
                        const maxJobs = Math.max(...data.trend.map(t => t.completedJobs || 0));
                        const width = maxJobs > 0 ? ((point.completedJobs || 0) / maxJobs) * 100 : 0;
                        return (
                          <div key={point.period || idx} className="flex items-center gap-3">
                            <div className="w-24 text-xs text-gray-500 shrink-0">{point.period}</div>
                            <div className="flex-1 bg-gray-200 rounded h-6 overflow-hidden">
                              <div
                                className="h-full bg-gradient-to-r from-blue-600 to-cyan-500 flex items-center justify-end pr-2"
                                style={{ width: `${width}%` }}
                              >
                                <span className="text-xs font-medium">{point.completedJobs}</span>
                              </div>
                            </div>
                            <div className="w-20 text-right text-xs text-gray-500">
                              {formatDuration(point.avgCycleTimeMinutes)} avg
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Trend table */}
                  <div className="bg-slate-100 rounded-lg overflow-hidden">
                    <table className="w-full">
                      <thead className="bg-white">
                        <tr>
                          <th className="text-left px-4 py-3 text-sm font-medium text-gray-500">Period</th>
                          <th className="text-right px-4 py-3 text-sm font-medium text-gray-500">Jobs</th>
                          <th className="text-right px-4 py-3 text-sm font-medium text-gray-500">Tickets</th>
                          <th className="text-right px-4 py-3 text-sm font-medium text-gray-500">Avg Cycle</th>
                          <th className="text-right px-4 py-3 text-sm font-medium text-gray-500">Total Hours</th>
                          <th className="text-right px-4 py-3 text-sm font-medium text-gray-500">Pieces</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {data.trend.slice().reverse().map((point, idx) => (
                          <tr key={point.period || idx} className="hover:bg-slate-100/30">
                            <td className="px-4 py-3 font-medium">{point.period}</td>
                            <td className="px-4 py-3 text-right text-blue-400 font-mono">
                              {point.completedJobs?.toLocaleString()}
                            </td>
                            <td className="px-4 py-3 text-right text-gray-700 font-mono">
                              {point.completedTickets?.toLocaleString()}
                            </td>
                            <td className="px-4 py-3 text-right text-purple-600">
                              {formatDuration(point.avgCycleTimeMinutes)}
                            </td>
                            <td className="px-4 py-3 text-right text-gray-700 font-mono">
                              {Math.round((point.totalSeconds || 0) / 3600).toLocaleString()}
                            </td>
                            <td className="px-4 py-3 text-right text-gray-500 font-mono">
                              {point.totalPieces?.toLocaleString() || 0}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </main>
    </div>
  );
}
